/**
 * Sistema de notificaciones para el dashboard
 * 
 * Proporciona feedback visual elegante para acciones del usuario
 * y actualizaciones del sistema.
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  Box,
  IconButton,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Context para el sistema de notificaciones
const NotificationContext = createContext();

/**
 * Hook para usar el sistema de notificaciones
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationProvider');
  }
  return context;
};

/**
 * Componente de transición personalizada
 */
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

/**
 * Componente individual de notificación
 */
const NotificationItem = ({ notification, onClose }) => {
  const theme = useTheme();
  
  const getIcon = (type) => {
    switch (type) {
      case 'success': return <SuccessIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      case 'refresh': return <RefreshIcon />;
      default: return <InfoIcon />;
    }
  };
  
  const getColor = (type) => {
    switch (type) {
      case 'success': return theme.palette.success.main;
      case 'error': return theme.palette.error.main;
      case 'warning': return theme.palette.warning.main;
      case 'info': return theme.palette.info.main;
      case 'refresh': return theme.palette.primary.main;
      default: return theme.palette.info.main;
    }
  };
  
  return (
    <Alert
      severity={notification.type === 'refresh' ? 'info' : notification.type}
      onClose={onClose}
      icon={getIcon(notification.type)}
      sx={{
        minWidth: 300,
        maxWidth: 500,
        boxShadow: theme.shadows[8],
        borderRadius: 2,
        border: `1px solid ${alpha(getColor(notification.type), 0.2)}`,
        '& .MuiAlert-icon': {
          color: getColor(notification.type)
        }
      }}
    >
      {notification.title && (
        <AlertTitle sx={{ fontWeight: 600 }}>
          {notification.title}
        </AlertTitle>
      )}
      <Typography variant="body2">
        {notification.message}
      </Typography>
      {notification.action && (
        <Box sx={{ mt: 1 }}>
          {notification.action}
        </Box>
      )}
    </Alert>
  );
};

/**
 * Provider del sistema de notificaciones
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  
  /**
   * Agregar una nueva notificación
   */
  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: 'info',
      autoHideDuration: 5000,
      ...notification
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Auto-remove si tiene duración
    if (newNotification.autoHideDuration) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.autoHideDuration);
    }
    
    return id;
  };
  
  /**
   * Remover una notificación
   */
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  
  /**
   * Limpiar todas las notificaciones
   */
  const clearNotifications = () => {
    setNotifications([]);
  };
  
  /**
   * Notificaciones predefinidas para casos comunes
   */
  const showSuccess = (message, title = '✅ Éxito', options = {}) => {
    return addNotification({
      type: 'success',
      title,
      message,
      ...options
    });
  };
  
  const showError = (message, title = '❌ Error', options = {}) => {
    return addNotification({
      type: 'error',
      title,
      message,
      autoHideDuration: 8000, // Errores duran más
      ...options
    });
  };
  
  const showWarning = (message, title = '⚠️ Advertencia', options = {}) => {
    return addNotification({
      type: 'warning',
      title,
      message,
      ...options
    });
  };
  
  const showInfo = (message, title = 'ℹ️ Información', options = {}) => {
    return addNotification({
      type: 'info',
      title,
      message,
      ...options
    });
  };
  
  const showRefresh = (message = 'Datos actualizados correctamente', title = '🔄 Actualizado', options = {}) => {
    return addNotification({
      type: 'refresh',
      title,
      message,
      autoHideDuration: 3000,
      ...options
    });
  };
  
  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showRefresh
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Render de notificaciones */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ 
            vertical: 'bottom', 
            horizontal: 'right' 
          }}
          sx={{
            bottom: 24 + (index * 80), // Stack vertical
            zIndex: theme => theme.zIndex.snackbar + index
          }}
        >
          <div>
            <NotificationItem
              notification={notification}
              onClose={() => removeNotification(notification.id)}
            />
          </div>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

/**
 * Hook para notificaciones de dashboard específicas
 */
export const useDashboardNotifications = () => {
  const { showSuccess, showError, showWarning, showInfo, showRefresh } = useNotifications();
  
  return {
    notifyDataRefresh: () => showRefresh('Dashboard actualizado con los datos más recientes'),
    notifyDataError: (error) => showError(`Error al cargar datos: ${error}`, '🚨 Error de Conexión'),
    notifyActionSuccess: (action) => showSuccess(`${action} ejecutado correctamente`),
    notifyActionError: (action, error) => showError(`Error al ejecutar ${action}: ${error}`),
    notifyStockAlert: (productName) => showWarning(`Stock crítico detectado en ${productName}`, '📦 Alerta de Stock'),
    notifyRecommendation: (message) => showInfo(message, '💡 Recomendación'),
    notifySystemUpdate: () => showInfo('Nuevas funcionalidades disponibles', '🆕 Actualización del Sistema')
  };
};

export default NotificationProvider;
